// src/pages/MaterialsPage.js
import React, { useState, useEffect } from 'react';
import { 
  Package, 
  Plus, 
  Grid, 
  ShoppingCart, 
  AlertCircle,
  PlusCircle,
  ChevronDown
} from 'lucide-react';
import { materialsAPI, projectsAPI } from '../services/api';

const MaterialsPage = () => {
  const [materials, setMaterials] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [materialEntries, setMaterialEntries] = useState([
    { 
      material_id: '', 
      quantity: '', 
      description: ''
    }
  ]);
  const [showNewMaterialModal, setShowNewMaterialModal] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [newMaterial, setNewMaterial] = useState({
    name: '',
    unit: '',
    price_per_unit: '0.00'
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [materialsResponse, projectsResponse] = await Promise.all([
          materialsAPI.getAll(),
          projectsAPI.getAll()
        ]);
        
        setMaterials(materialsResponse.data.materials);
        setProjects(projectsResponse.data.projects);
        setError('');
      } catch (err) {
        setError('Fehler beim Laden der Daten');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleMaterialChange = (index, field, value) => {
    const newEntries = [...materialEntries];
    newEntries[index][field] = value;
    setMaterialEntries(newEntries);
  };

  const handleNewMaterialChange = (e) => {
    const { name, value } = e.target;
  
    if (name === 'price_per_unit') {
      const formattedValue = value === '' 
        ? '0.00' 
        : (parseFloat(value.replace(',', '.')) || 0).toFixed(2);
      
      setNewMaterial(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setNewMaterial(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const addMaterialEntry = () => {
    setMaterialEntries([
      ...materialEntries, 
      { 
        material_id: '', 
        quantity: '', 
        description: ''
      }
    ]);
  };

  const createNewMaterial = async (e) => {
    e.preventDefault();
    try {
      const response = await materialsAPI.create({
        ...newMaterial,
        price_per_unit: parseFloat(newMaterial.price_per_unit)
      });
      
      setMaterials([...materials, response.data.material]);
      
      setShowNewMaterialModal(false);
      setNewMaterial({
        name: '',
        unit: '',
        price_per_unit: '0.00'
      });
    } catch (err) {
      setError('Fehler beim Erstellen des Materials');
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedProject) {
      setError('Bitte wählen Sie einen Auftrag aus');
      return;
    }

    try {
      await Promise.all(
        materialEntries
          .filter(entry => entry.material_id && entry.quantity)
          .map(entry => {
            // Finde den Preis des ausgewählten Materials
            const material = materials.find(m => m.id === entry.material_id);
            return materialsAPI.logMaterialUsage({
              project_id: selectedProject,
              material_id: entry.material_id,
              quantity: entry.quantity,
              description: entry.description,
              price: material ? material.price_per_unit : '0.00'
            });
          })
      );

      setMaterialEntries([{ 
        material_id: '', 
        quantity: '', 
        description: ''
      }]);
      setSelectedProject('');
      setError('Materialien erfolgreich gespeichert');
    } catch (err) {
      setError('Fehler beim Speichern der Materialien');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Package className="h-10 w-10 sm:h-12 sm:w-12 text-primary-600 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen p-4 sm:p-8">
      <div className="container mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center mb-4 sm:mb-0">
            <ShoppingCart className="mr-2 sm:mr-4 h-6 w-6 sm:h-6 sm:w-6 text-primary-600" />
            Materialverbrauch buchen
          </h1>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 mb-4 sm:mb-6 flex items-center">
            <AlertCircle className="mr-2 sm:mr-3 h-5 w-5 text-red-500" />
            <p className="text-sm sm:text-base text-red-700">{error}</p>
          </div>
        )}

        {showNewMaterialModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl w-full max-w-sm">
              <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 flex items-center">
                <PlusCircle className="mr-2 sm:mr-3 h-5 w-5 text-primary-600" />
                Neues Material anlegen
              </h2>
              <form onSubmit={createNewMaterial}>
                <div className="space-y-3 sm:space-y-4">
                  <input
                    type="text"
                    name="name"
                    value={newMaterial.name}
                    onChange={handleNewMaterialChange}
                    placeholder="Materialname *"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    name="unit"
                    value={newMaterial.unit}
                    onChange={handleNewMaterialChange}
                    placeholder="Einheit (z.B. Stück, kg)"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    name="price_per_unit"
                    value={newMaterial.price_per_unit}
                    onChange={handleNewMaterialChange}
                    placeholder="Preis pro Einheit *"
                    pattern="^\d+(\.\d{1,2})?$"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowNewMaterialModal(false)}
                      className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="submit"
                      className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition"
                    >
                      Material anlegen
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <form onSubmit={handleSubmit}>
            <div className="mb-4 relative">
              <label className="block text-sm sm:text-base text-gray-700 mb-1 sm:mb-2">Auftrag auswählen *</label>
              <div 
                onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
                className="w-full px-3 py-2 border border-gray-300 rounded flex justify-between items-center cursor-pointer"
              >
                {selectedProject 
                  ? projects.find(p => p.id === selectedProject)?.title || 'Auftrag wählen'
                  : '-- Auftrag wählen --'}
                <ChevronDown className="h-5 w-5 text-gray-500" />
              </div>
              {isProjectDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
                  {projects.map((project) => (
                    <div 
                      key={project.id}
                      onClick={() => {
                        setSelectedProject(project.id);
                        setIsProjectDropdownOpen(false);
                      }}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    >
                      {project.title} ({project.company_name || `${project.first_name} ${project.last_name}`})
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3 sm:space-y-4 mt-4">
              {materialEntries.map((entry, index) => (
                <div key={index} className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                  <select
                    value={entry.material_id}
                    onChange={(e) => handleMaterialChange(index, 'material_id', e.target.value)}
                    className="w-full sm:w-3/4 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Material wählen</option>
                    {materials.map((material) => (
                      <option key={material.id} value={material.id}>
                        {material.name} ({material.unit}) - CHF {material.price_per_unit} pro {material.unit}
                      </option>
                    ))}
                  </select>
                  
                  <div className="flex space-x-3">
                    <input
                      type="number"
                      step="0.01"
                      value={entry.quantity}
                      onChange={(e) => handleMaterialChange(index, 'quantity', e.target.value)}
                      placeholder="Menge"
                      className="w-1/3 sm:w-1/4 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                    
                    <input
                      type="text"
                      value={entry.description}
                      onChange={(e) => handleMaterialChange(index, 'description', e.target.value)}
                      placeholder="Beschreibung"
                      className="w-2/3 sm:w-1/4 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between space-y-4 sm:space-y-0 mt-4">
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <button 
                  type="button" 
                  onClick={addMaterialEntry}
                  className="text-primary-600 hover:text-primary-800 flex items-center w-full sm:w-auto justify-center sm:justify-start"
                >
                  <Plus className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
                  Weitere Zeile
                </button>

                <button 
                  type="button"
                  onClick={() => setShowNewMaterialModal(true)}
                  className="text-primary-600 hover:text-primary-800 flex items-center w-full sm:w-auto justify-center sm:justify-start"
                >
                  <Grid className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
                  Neues Material
                </button>
              </div>

              <button
                type="submit"
                disabled={!selectedProject}
                className={`bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 transition w-full sm:w-auto ${!selectedProject ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Materialverbrauch speichern
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MaterialsPage;
